import authService from '../utils/auth.js';
import { getApiBaseUrl } from '../utils/apiBase.js';

export class SignPdfError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.name = 'SignPdfError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Apply a visual signature stamp to a PDF.
 * @param {{ pdfFile: File, signatureBlob: Blob, page: number, x: number, y: number, width: number, height: number }} params
 * @returns {Promise<{ blob: Blob, filename: string, sha256: string|null }>}
 */
export async function signPdf({ pdfFile, signatureBlob, page, x, y, width, height, angle = 0 }) {
  const form = new FormData();
  form.append('pdf', pdfFile, pdfFile.name || 'document.pdf');
  form.append('signature', signatureBlob, 'signature.png');
  form.append('page', String(page));
  form.append('x', String(x));
  form.append('y', String(y));
  form.append('width', String(width));
  form.append('height', String(height));
  form.append('angle', String(angle || 0));

  const token = authService.getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${getApiBaseUrl()}/api/tools/sign-pdf`, {
    method: 'POST',
    headers,
    body: form
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new SignPdfError(
      data.message || `Could not sign PDF (${response.status})`,
      { code: data.code, status: response.status }
    );
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || 'document-signed.pdf';
  const sha256 = response.headers.get('X-NovaBotX-Sign-Sha256');

  return { blob, filename, sha256 };
}
